// ============================================================
//  ALTIMO Medical Center — ModalConfirmar.jsx
//  Componente reutilizable de confirmación (reemplaza window.confirm)
//  Ubicación: src/components/ModalConfirmar.jsx
//
//  Uso típico (reemplaza 1 línea, mismo flujo de "return temprano"):
//
//    const { confirmar, ModalConfirmarUI } = useConfirmar();
//    ...
//    if (!(await confirmar({ texto: '¿Eliminar este registro?' }))) return;
//    ...
//    return ( <>
//      {ModalConfirmarUI}
//      ...resto del JSX...
//    </> );
//
//  Modo "alto riesgo" (requiere escribir una palabra exacta para habilitar
//  el botón de confirmar — usar en eliminaciones irreversibles / datos clínicos):
//
//    await confirmar({
//      texto: '¿Eliminar permanentemente este expediente?',
//      tipo: 'peligro',
//      palabraConfirmacion: 'ELIMINAR',
//    });
// ============================================================

import { useState, useCallback, useRef } from 'react';

// ── Paleta ALTIMO (consistente con App.jsx / Dashboard.jsx) ──
const COLOR = {
  azul:        '#1B2A5E',
  azulClaro:   '#e8f0fb',
  verde:       '#1D9E75',
  verdeClaro:  '#d4edda',
  rojo:        '#dc3545',
  rojoClaro:   '#f8d7da',
  amarillo:    '#ffc107',
  amarilloClaro:'#fff3cd',
  gris:        '#6c757d',
  borde:       '#dde3ea',
};

// tipo: 'normal' (azul, acción estándar) | 'peligro' (rojo, irreversible) | 'advertencia' (amarillo)
const TIPO_CFG = {
  normal: {
    icono: '❓',
    colorTitulo: COLOR.azul,
    colorBoton: COLOR.azul,
    tituloDefault: 'Confirmar acción',
  },
  peligro: {
    icono: '⚠️',
    colorTitulo: COLOR.rojo,
    colorBoton: COLOR.rojo,
    tituloDefault: 'Acción irreversible',
  },
  advertencia: {
    icono: '⚠️',
    colorTitulo: '#856404',
    colorBoton: COLOR.amarillo,
    tituloDefault: 'Advertencia',
  },
};

function ModalConfirmar({ config, onResolver }) {
  const {
    titulo,
    texto,
    tipo = 'normal',
    textoConfirmar = 'Confirmar',
    textoCancelar = 'Cancelar',
    palabraConfirmacion = null,
  } = config;

  const cfg = TIPO_CFG[tipo] || TIPO_CFG.normal;
  const [inputPalabra, setInputPalabra] = useState('');
  const requierePalabra = !!palabraConfirmacion;
  const habilitado = !requierePalabra || inputPalabra.trim() === palabraConfirmacion;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)',
        zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Segoe UI, sans-serif',
      }}
      onClick={() => onResolver(false)}
    >
      <div
        style={{
          background: '#fff', borderRadius: '16px', padding: '32px 36px',
          maxWidth: '440px', width: '90%', boxShadow: '0 12px 48px rgba(0,0,0,.3)',
          textAlign: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: '42px', marginBottom: '10px' }}>{cfg.icono}</div>

        <div style={{ fontSize: '16px', fontWeight: '800', marginBottom: '10px', color: cfg.colorTitulo }}>
          {titulo || cfg.tituloDefault}
        </div>

        <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.6', marginBottom: requierePalabra ? '18px' : '26px', whiteSpace: 'pre-line' }}>
          {texto}
        </div>

        {requierePalabra && (
          <div style={{ marginBottom: '22px', textAlign: 'left' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: COLOR.gris, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              Escribe <span style={{ color: cfg.colorTitulo }}>{palabraConfirmacion}</span> para confirmar
            </label>
            <input
              autoFocus
              value={inputPalabra}
              onChange={(e) => setInputPalabra(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && habilitado) onResolver(true); }}
              style={{
                width: '100%', boxSizing: 'border-box', fontSize: '14px', padding: '10px 13px',
                border: `1.5px solid ${COLOR.borde}`, borderRadius: '8px', outline: 'none',
                fontFamily: 'Segoe UI, sans-serif',
              }}
              placeholder={palabraConfirmacion}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            onClick={() => onResolver(false)}
            style={{
              background: '#fff', color: COLOR.gris, border: `1.5px solid ${COLOR.borde}`,
              borderRadius: '8px', padding: '11px 24px', fontSize: '14px', fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {textoCancelar}
          </button>
          <button
            onClick={() => habilitado && onResolver(true)}
            disabled={!habilitado}
            style={{
              background: habilitado ? cfg.colorBoton : '#ccc', color: '#fff', border: 'none',
              borderRadius: '8px', padding: '11px 28px', fontSize: '14px', fontWeight: '700',
              cursor: habilitado ? 'pointer' : 'not-allowed',
            }}
          >
            {textoConfirmar}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Hook ───────────────────────────────────────────────────────
// Devuelve:
//   confirmar(config) -> Promise<boolean>   (true si el usuario confirmó)
//   ModalConfirmarUI                        (JSX a renderizar una vez en el componente)
export function useConfirmar() {
  const [config, setConfig] = useState(null);
  const resolverRef = useRef(null);

  const confirmar = useCallback((cfg) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setConfig(typeof cfg === 'string' ? { texto: cfg } : cfg);
    });
  }, []);

  const onResolver = useCallback((valor) => {
    setConfig(null);
    if (resolverRef.current) {
      resolverRef.current(valor);
      resolverRef.current = null;
    }
  }, []);

  const ModalConfirmarUI = config
    ? <ModalConfirmar config={config} onResolver={onResolver} />
    : null;

  return { confirmar, ModalConfirmarUI };
}

export default ModalConfirmar;
